import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { editMember, savemembershippayment } from '../store/slices/userSlice';
import { successMessage, errorMessage } from '../utils/tostify';
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";

// Ensure window.paypal is declared globally for TypeScript/ESLint if not already
// declare global {
//   interface Window {
//     paypal: any;
//   }
// }

function RenewPaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [subscriptionID, setSubscriptionID] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // useRef to track if PayPal buttons have been successfully rendered to prevent multiple renders
  const paypalRenderedRef = useRef(false);
  // useRef to track if the script loading process has started to prevent multiple script appends
  const scriptLoadingRef = useRef(false);

  // Destructure values from location.state
  const { membershipTypeId, membershipTypeName, membershipTypeAmount, newValidUpto, paypalPlanId } = location.state || {};
  const userId = localStorage.getItem('id');

  // Helper function to unwrap Redux Toolkit async thunk results
  const unwrapResult = useCallback((result) => {
    if (result.payload) {
      return result.payload;
    } else if (result.error) {
      throw result.error;
    }
    return result;
  }, []);

  // Countdown logic for auto-redirection after successful payment
  const startCountdownAndRedirect = useCallback(() => {
    try {
      let count = 5;
      setCountdown(count);
      const timer = setInterval(() => {
        try {
          count--;
          setCountdown(count);
          if (count === 0) {
            clearInterval(timer);
            navigate('/dashboard');
          }
        } catch (innerError) {
          console.error("Error during countdown interval:", innerError);
          clearInterval(timer); // ensure the interval is stopped
        }
      }, 1000);
    } catch (error) {
      console.error("Error starting countdown:", error);
    }
  }, [navigate]);
  
  /**
   * Cleans up any existing PayPal SDK script and global objects.
   */
  const cleanupPayPalSDK = useCallback(() => {
    try {
      console.log("Cleanup: Starting PayPal SDK cleanup.");
  
      const scriptId = 'paypal-sdk-script';
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
        console.log("Cleanup: Removed existing PayPal SDK script.");
      }
  
      // Aggressively remove PayPal global objects
      if (window.paypal) {
        Object.keys(window).forEach(key => {
          if (key.startsWith('paypal')) {
            try {
              delete window[key];
            } catch (e) {
              console.warn(`Cleanup: Could not delete window.${key}:`, e);
            }
          }
        });
        console.log("Cleanup: Attempted to clear all window.paypal related objects.");
      }
  
      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = '';
        console.log("Cleanup: Cleared PayPal button container.");
      }
  
      paypalRenderedRef.current = false;
      scriptLoadingRef.current = false;
  
      console.log("Cleanup: PayPal SDK cleanup complete.");
    } catch (error) {
      console.error("Cleanup: Unexpected error during PayPal SDK cleanup:", error);
    }
  }, []);
  

  useEffect(() => {
    // If essential data is missing, redirect back
    if (!membershipTypeId || !newValidUpto || !paypalPlanId || !userId) {
      errorMessage("Missing membership or user information. Please select membership again.");
      navigate('/renew-membership');
      return;
    }

    // Ensure we don't try to load the script multiple times during a single component lifecycle
    // or if it's already successfully rendered.
    if (paypalRenderedRef.current || scriptLoadingRef.current) {
      console.log("PayPal buttons already rendered or script loading in progress, skipping re-initialization.");
      setLoading(false);
      return;
    }

    scriptLoadingRef.current = true; // Mark script loading as started

    /**
     * Handles the entire process of loading the PayPal SDK and rendering the buttons.
     * This function is designed to be idempotent and robust against multiple calls.
     */
    const loadAndRenderPayPalButtons = async () => {
      setLoading(true); // Set loading state to true while attempting to load/render
      try {
        // Always run cleanup before attempting to load/render
        cleanupPayPalSDK(); // Call the dedicated cleanup function

        const container = document.getElementById('paypal-button-container');
        if (!container) {
          console.error("PayPal button container not found in DOM. Cannot proceed.");
          errorMessage("Payment container not found. Please refresh the page.");
          setStatus('fail');
          setLoading(false);
          scriptLoadingRef.current = false;
          return; // Exit if container is not found
        }

        const scriptId = 'paypal-sdk-script';
        const script = document.createElement('script');
        script.id = scriptId;
        // Use a dynamic client-id if it can change, otherwise it's fine as is.
        script.src = `https://www.paypal.com/sdk/js?client-id=AYB2yrW-VqMF7hAxqkXp4KZP-wLFomi24wBCLVQB3hna7V2mCdqZz4e4V3muCnlVw_6VcD4yhXQY0Rp8&vault=true&intent=subscription`;
        script.setAttribute('data-sdk-integration-source', 'button-factory');
        script.async = true;
        document.body.appendChild(script);
        console.log("PayPal SDK script appended to body.");

        // Wait for the SDK to be fully loaded and ready
        await new Promise((resolve, reject) => {
          script.onload = () => {
            console.log("PayPal SDK script loaded. Starting check for window.paypal.Buttons...");
            const checkReady = setInterval(() => {
              if (window.paypal && typeof window.paypal.Buttons === 'function') {
                clearInterval(checkReady);
                console.log("PayPal SDK loaded and ready (Buttons function found).");
                resolve();
              }
            }, 100);
          };
          script.onerror = (error) => {
            console.error("Failed to load PayPal SDK script:", error);
            reject(new Error("PayPal SDK script failed to load."));
          };
        });

        console.log("Attempting to render PayPal buttons...");
        window.paypal.Buttons({
          style: {
            shape: 'pill',
            color: 'gold',
            layout: 'vertical',
            label: 'subscribe',
          },
          createSubscription: function (data, actions) {
            if (!paypalPlanId) {
              errorMessage("PayPal Plan ID is missing for this membership type.");
              throw new Error("PayPal Plan ID missing.");
            }
            console.log("Creating subscription with plan_id:", paypalPlanId);
            return actions.subscription.create({
              plan_id: paypalPlanId,
            });
          },
          onApprove: async function (data, actions) {
            if (data.subscriptionID) {
              setSubscriptionID(data.subscriptionID);
              setStatus('success');
              successMessage("Payment successful! Updating membership status...");
              console.log("PayPal subscription approved. ID:", data.subscriptionID);

              try {
                const savePaymentResult = await dispatch(savemembershippayment({
                  member: userId,
                  orderid: data.subscriptionID,
                  paymentstatus: 'COMPLETED',
                  amount: membershipTypeAmount,
                  paymentmode: 'paypal',
                  subscription: membershipTypeId,
                  date: new Date().toISOString().split('T')[0]
                }));
                unwrapResult(savePaymentResult);
                console.log("Payment record saved successfully.");
              } catch (paymentError) {
                console.error("Failed to save payment record:", paymentError);
                errorMessage("Payment successful, but failed to record payment details in backend.");
              }

              try {
                const updateMemberResult = await dispatch(editMember({
                  id: userId,
                  validupto: newValidUpto,
                  status: "1"
                }));
                unwrapResult(updateMemberResult);
                console.log("Member validupto updated successfully after payment.");
                successMessage("Membership successfully renewed!");
                startCountdownAndRedirect();
              } catch (memberUpdateError) {
                console.error("Failed to update member record after payment:", memberUpdateError);
                errorMessage("Payment successful, but failed to update your membership in backend.");
                setStatus('fail');
              }
            } else {
              setStatus('fail');
              errorMessage("PayPal subscription ID not returned.");
              console.error("PayPal subscription ID missing from onApprove data.");
            }
          },
          onCancel: function () {
            setStatus('cancel');
            errorMessage("PayPal subscription cancelled by user.");
            console.log("PayPal subscription cancelled.");
          },
          onError: function (err) {
            console.error('PayPal Error:', err);
            setStatus('fail');
            errorMessage("An error occurred during PayPal payment. Please try again.");
          }
        }).render('#paypal-button-container');
        console.log("PayPal buttons rendered successfully.");
        paypalRenderedRef.current = true; // Mark as rendered successfully
      } catch (e) {
        console.error("Error during PayPal button initialization:", e);
        setStatus('fail');
        errorMessage("Failed to load PayPal payment options.");
        // Ensure to reset scriptLoadingRef if an error occurs during loading
        scriptLoadingRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    // Call the initialization function when the component mounts
    loadAndRenderPayPalButtons();

    // Cleanup function for when the component unmounts
    return () => {
      console.log("Running cleanup during component unmount for RenewPaymentPage.");
      cleanupPayPalSDK(); // Ensure cleanup runs on unmount
    };
  }, [
    dispatch,
    navigate,
    membershipTypeId,
    membershipTypeName,
    membershipTypeAmount,
    newValidUpto,
    paypalPlanId,
    userId,
    unwrapResult,
    startCountdownAndRedirect,
    cleanupPayPalSDK // Add cleanupPayPalSDK to dependencies as it's a useCallback
  ]);

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div id="main-wrapper" className="flex flex-col min-h-screen">
        <div className="page-wrapper flex-grow d-block">
          <div className="body-wrapper">
            <Container fluid className="h-full">
              <div className="folio-container"></div>
              <Row className="justify-content-center items-center h-full min-h-[calc(100vh-60px)]">
                <Col lg={6} md={8}>
                  <Card className="w-100 rounded-xl shadow-lg">
                    <Card.Header style={{ background: "#f06969" }} className="rounded-t-xl">
                      <div className="d-sm-flex d-block align-items-center justify-content-between mb-4">
                        <h4 className="card-title fw-semibold text-white text-2xl">Membership Payment</h4>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-6">
                      <div className="flex flex-col items-center justify-center bg-gray-100 p-4 rounded-b-xl font-inter">
                        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md text-center">
                          <h2 className="text-3xl font-bold text-gray-800 mb-6">Complete Your Subscription</h2>
                          <p className="text-lg text-gray-600 mb-4">
                            Selected Membership: <strong>{membershipTypeName || 'N/A'}</strong> (Valid until: <strong>{newValidUpto || 'N/A'}</strong>)
                            <br />Amount: <strong>${membershipTypeAmount ? parseFloat(membershipTypeAmount).toFixed(2) : '0.00'}</strong>
                          </p>

                          {loading && (
                            <div className="text-blue-600 text-lg mb-4">Loading PayPal buttons...</div>
                          )}

                          {/* PayPal button container */}
                          <div id="paypal-button-container" className="mb-6"></div>

                          {/* Payment Status Messages */}
                          {status === 'success' && (
                            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4">
                              <strong className="font-bold">✅ Payment Successful!</strong>
                              <span className="block sm:inline"> Your subscription is now active.</span>
                              {subscriptionID && (
                                <p className="text-sm mt-2">Subscription ID: <code className="font-mono bg-green-200 p-1 rounded">{subscriptionID}</code></p>
                              )}
                              <p className="mt-3">Redirecting to dashboard in {countdown} seconds...</p>
                            </div>
                          )}

                          {status === 'fail' && (
                            <div className="bg-red-100 text-red-700 p-4 rounded-lg font-semibold flex items-center justify-center gap-2">
                              <span className="text-2xl">❌</span> Payment Failed. Please try again or contact support.
                            </div>
                          )}

                          {status === 'cancel' && (
                            <div className="bg-orange-100 text-orange-700 p-4 rounded-lg font-semibold flex items-center justify-center gap-2">
                              <span className="text-2xl">⚠️</span> Subscription was cancelled by the user.
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RenewPaymentPage;